import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import {
  type AthleteView,
  type CompetitionSeasonView,
  type PageResponse,
  type SuspensionView,
  type SupportedLocale,
} from '@stm/contracts';
import { translate, type TranslationKey } from '@stm/i18n';
import { apiRequest } from './api';
import { athleteDisplayName, formatCalendarDate } from './athletes';
import { errorKeyOf, type ErrorKey } from './BillingForms';
import { RemoteSelect, type RemoteOption } from './RemoteSelect';
import {
  activeFirst,
  emptySuspensionForm,
  maximumSuspensionGames,
  revokeRequest,
  serveRequest,
  suspensionCorrectionFormOf,
  suspensionCorrectionRequest,
  suspensionPath,
  suspensionRequest,
  suspensionStatusKeys,
  type SuspensionCorrectionForm,
  type SuspensionForm,
  type SuspensionMeasure,
} from './suspensions';

export interface SuspensionsSectionProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}`. */
  base: string;
  csrfToken: string;
  competition: CompetitionSeasonView;
}

type OpenPanel =
  | { kind: 'correct'; suspensionId: string; form: SuspensionCorrectionForm }
  | { kind: 'serve'; suspensionId: string; note: string }
  | { kind: 'revoke'; suspensionId: string; reason: string };

function initialForm(competition: CompetitionSeasonView): SuspensionForm {
  return { ...emptySuspensionForm, competitionSeasonId: competition.id };
}

/**
 * Suspensions that apply in one competition: those for this competition and those for every
 * competition of the club. A registrar records a suspension that runs until a day or counts games,
 * corrects it while it is active, marks it served or revokes it with a reason. Only an active
 * suspension counts towards eligibility; a served or revoked one stays as history.
 */
export function SuspensionsSection({
  locale,
  base,
  csrfToken,
  competition,
}: SuspensionsSectionProps): React.JSX.Element {
  const [records, setRecords] = useState<SuspensionView[] | null>(null);
  const [form, setForm] = useState<SuspensionForm>(() => initialForm(competition));
  const [formKey, setFormKey] = useState(0);
  const [panel, setPanel] = useState<OpenPanel | null>(null);
  const [errorKey, setErrorKey] = useState<ErrorKey | null>(null);
  const [message, setMessage] = useState<TranslationKey | null>(null);

  const load = useCallback(async () => {
    try {
      setRecords(
        await apiRequest<SuspensionView[]>(
          `${suspensionPath(base)}?competitionSeasonId=${encodeURIComponent(competition.id)}`,
        ),
      );
    } catch (error) {
      setErrorKey(errorKeyOf(error));
    }
  }, [base, competition.id]);

  useEffect(() => {
    setRecords(null);
    setPanel(null);
    setErrorKey(null);
    setMessage(null);
    setForm(initialForm(competition));
    setFormKey((current) => current + 1);
    void load();
  }, [load, competition]);

  const findAthletes = useCallback(
    async (text: string): Promise<RemoteOption[]> => {
      const page = await apiRequest<PageResponse<AthleteView>>(
        `${base}/athletes?limit=20&q=${encodeURIComponent(text)}`,
      );
      return page.items.map((athlete) => ({
        id: athlete.id,
        label: `${athleteDisplayName(athlete, locale)} (${formatCalendarDate(athlete.dateOfBirth, locale)})`,
      }));
    },
    [base, locale],
  );

  async function submit(action: () => Promise<unknown>, successKey: TranslationKey): Promise<void> {
    setErrorKey(null);
    setMessage(null);
    try {
      await action();
      setMessage(successKey);
      setPanel(null);
      await load();
    } catch (error) {
      setErrorKey(errorKeyOf(error));
      // A version conflict or a suspension closed meanwhile: show what the server holds now.
      await load();
    }
  }

  const createRequest = suspensionRequest(form);

  async function record(): Promise<void> {
    if (createRequest === null) return;
    await submit(async () => {
      await apiRequest<SuspensionView>(suspensionPath(base), {
        method: 'POST',
        body: createRequest,
        csrfToken,
      });
      setForm(initialForm(competition));
      setFormKey((current) => current + 1);
    }, 'suspension.done');
  }

  async function command(
    suspension: SuspensionView,
    request: { method: 'PATCH' | 'POST'; action?: 'serve' | 'revoke'; body: unknown },
    successKey: TranslationKey,
  ): Promise<void> {
    await submit(async () => {
      await apiRequest<SuspensionView>(suspensionPath(base, suspension.id, request.action), {
        method: request.method,
        body: request.body,
        csrfToken,
        ifMatchVersion: suspension.version,
      });
    }, successKey);
  }

  function measureText(suspension: SuspensionView): string {
    if (suspension.endsOn !== null) {
      return `${translate(locale, 'suspension.until')} ${formatCalendarDate(suspension.endsOn, locale)}`;
    }
    return `${translate(locale, 'suspension.gamesLeft')}: ${String(suspension.remainingGames ?? 0)}`;
  }

  const cancelButton = (
    <Button
      size="small"
      onClick={() => {
        setPanel(null);
      }}
    >
      {translate(locale, 'suspension.cancel')}
    </Button>
  );

  return (
    <Box component="section" aria-labelledby="suspensions-title" sx={{ mt: 3 }}>
      <Typography variant="h6" component="h3" id="suspensions-title" gutterBottom>
        {translate(locale, 'suspension.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate(locale, 'suspension.hint')}
      </Typography>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {message !== null && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {translate(locale, message)}
        </Alert>
      )}
      {records === null && errorKey === null && (
        <Typography>{translate(locale, 'suspension.loading')}</Typography>
      )}
      {records?.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {translate(locale, 'suspension.empty')}
        </Typography>
      )}
      <Stack component="ul" spacing={2} sx={{ listStyle: 'none', padding: 0, margin: 0, mb: 3 }}>
        {activeFirst(records ?? []).map((suspension) => {
          const open = panel?.suspensionId === suspension.id ? panel : null;
          const correction =
            open?.kind === 'correct' ? suspensionCorrectionRequest(open.form, suspension) : null;
          const revocation = open?.kind === 'revoke' ? revokeRequest(open.reason) : null;
          return (
            <Box component="li" key={suspension.id}>
              <Typography variant="subtitle2">
                {athleteDisplayName(suspension.athlete, locale)} ·{' '}
                {translate(locale, suspensionStatusKeys[suspension.status])}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatCalendarDate(suspension.startsOn, locale)} · {measureText(suspension)} ·{' '}
                {suspension.competition === null
                  ? translate(locale, 'suspension.everyCompetition')
                  : suspension.competition.name}
                {suspension.decisionReference === null
                  ? ''
                  : ` · ${translate(locale, 'suspension.reference')}: ${suspension.decisionReference}`}
              </Typography>
              {suspension.closingNote !== null && (
                <Typography variant="body2" color="text.secondary">
                  {translate(locale, 'suspension.closingNote')}: {suspension.closingNote}
                </Typography>
              )}
              {suspension.canManage && suspension.status === 'active' && open === null && (
                <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setPanel({ kind: 'serve', suspensionId: suspension.id, note: '' });
                    }}
                  >
                    {translate(locale, 'suspension.serve')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setPanel({
                        kind: 'correct',
                        suspensionId: suspension.id,
                        form: suspensionCorrectionFormOf(suspension),
                      });
                    }}
                  >
                    {translate(locale, 'suspension.correct')}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setPanel({ kind: 'revoke', suspensionId: suspension.id, reason: '' });
                    }}
                  >
                    {translate(locale, 'suspension.revoke')}
                  </Button>
                </Box>
              )}
              {open?.kind === 'serve' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    void command(
                      suspension,
                      { method: 'POST', action: 'serve', body: serveRequest(open.note) },
                      'suspension.serve.done',
                    );
                  }}
                >
                  <Typography variant="body2">
                    {translate(locale, 'suspension.serve.hint')}
                  </Typography>
                  <TextField
                    size="small"
                    label={translate(locale, 'suspension.serve.note')}
                    value={open.note}
                    onChange={(event) => {
                      setPanel({ ...open, note: event.target.value });
                    }}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button type="submit" variant="contained" size="small">
                      {translate(locale, 'suspension.serve.submit')}
                    </Button>
                    {cancelButton}
                  </Box>
                </Stack>
              )}
              {open?.kind === 'revoke' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (revocation !== null) {
                      void command(
                        suspension,
                        { method: 'POST', action: 'revoke', body: revocation },
                        'suspension.revoke.done',
                      );
                    }
                  }}
                >
                  <Typography variant="body2">
                    {translate(locale, 'suspension.revoke.hint')}
                  </Typography>
                  <TextField
                    required
                    size="small"
                    label={translate(locale, 'suspension.revoke.reason')}
                    value={open.reason}
                    onChange={(event) => {
                      setPanel({ ...open, reason: event.target.value });
                    }}
                    slotProps={{ htmlInput: { maxLength: 500 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      color="error"
                      size="small"
                      disabled={revocation === null}
                    >
                      {translate(locale, 'suspension.revoke.submit')}
                    </Button>
                    {cancelButton}
                  </Box>
                </Stack>
              )}
              {open?.kind === 'correct' && (
                <Stack
                  component="form"
                  spacing={1.5}
                  sx={{ mt: 1, maxWidth: 420 }}
                  onSubmit={(event) => {
                    event.preventDefault();
                    if (correction !== null) {
                      void command(
                        suspension,
                        { method: 'PATCH', body: correction },
                        'suspension.correct.done',
                      );
                    }
                  }}
                >
                  <TextField
                    required
                    size="small"
                    type="date"
                    label={translate(locale, 'suspension.field.startsOn')}
                    value={open.form.startsOn}
                    onChange={(event) => {
                      setPanel({ ...open, form: { ...open.form, startsOn: event.target.value } });
                    }}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  {suspension.endsOn !== null ? (
                    <TextField
                      required
                      size="small"
                      type="date"
                      label={translate(locale, 'suspension.field.endsOn')}
                      value={open.form.endsOn}
                      onChange={(event) => {
                        setPanel({ ...open, form: { ...open.form, endsOn: event.target.value } });
                      }}
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  ) : (
                    <TextField
                      required
                      size="small"
                      label={translate(locale, 'suspension.field.games')}
                      value={open.form.remainingGames}
                      onChange={(event) => {
                        setPanel({
                          ...open,
                          form: { ...open.form, remainingGames: event.target.value },
                        });
                      }}
                      slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 2 } }}
                    />
                  )}
                  <TextField
                    size="small"
                    label={translate(locale, 'suspension.field.reference')}
                    value={open.form.decisionReference}
                    onChange={(event) => {
                      setPanel({
                        ...open,
                        form: { ...open.form, decisionReference: event.target.value },
                      });
                    }}
                    slotProps={{ htmlInput: { maxLength: 100 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="small"
                      disabled={correction === null}
                    >
                      {translate(locale, 'suspension.correct.submit')}
                    </Button>
                    {cancelButton}
                  </Box>
                </Stack>
              )}
            </Box>
          );
        })}
      </Stack>
      {competition.canManage ? (
        <Stack
          component="form"
          key={formKey}
          spacing={2}
          sx={{ maxWidth: 480 }}
          onSubmit={(event) => {
            event.preventDefault();
            void record();
          }}
        >
          <Typography variant="subtitle1" component="h4">
            {translate(locale, 'suspension.create.title')}
          </Typography>
          <RemoteSelect
            label={translate(locale, 'suspension.field.athlete')}
            noMatchesText={translate(locale, 'suspension.noAthletes')}
            load={findAthletes}
            onSelect={(athleteId) => {
              setForm((current) => ({ ...current, athleteId }));
            }}
          />
          <TextField
            select
            size="small"
            label={translate(locale, 'suspension.field.scope')}
            value={form.competitionSeasonId}
            onChange={(event) => {
              setForm({ ...form, competitionSeasonId: event.target.value });
            }}
          >
            <MenuItem value={competition.id}>{competition.name}</MenuItem>
            <MenuItem value="">{translate(locale, 'suspension.everyCompetition')}</MenuItem>
          </TextField>
          <TextField
            required
            select
            size="small"
            label={translate(locale, 'suspension.field.measure')}
            value={form.measure}
            onChange={(event) => {
              const measure: SuspensionMeasure = event.target.value === 'days' ? 'days' : 'games';
              setForm({ ...form, measure });
            }}
          >
            <MenuItem value="games">{translate(locale, 'suspension.measure.games')}</MenuItem>
            <MenuItem value="days">{translate(locale, 'suspension.measure.days')}</MenuItem>
          </TextField>
          <TextField
            required
            size="small"
            type="date"
            label={translate(locale, 'suspension.field.startsOn')}
            value={form.startsOn}
            onChange={(event) => {
              setForm({ ...form, startsOn: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {form.measure === 'days' ? (
            <TextField
              required
              size="small"
              type="date"
              label={translate(locale, 'suspension.field.endsOn')}
              value={form.endsOn}
              onChange={(event) => {
                setForm({ ...form, endsOn: event.target.value });
              }}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          ) : (
            <TextField
              required
              size="small"
              label={translate(locale, 'suspension.field.games')}
              helperText={translate(locale, 'suspension.field.gamesHint').replace(
                '{maximum}',
                String(maximumSuspensionGames),
              )}
              value={form.remainingGames}
              onChange={(event) => {
                setForm({ ...form, remainingGames: event.target.value });
              }}
              slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 2 } }}
            />
          )}
          <TextField
            size="small"
            label={translate(locale, 'suspension.field.reference')}
            value={form.decisionReference}
            onChange={(event) => {
              setForm({ ...form, decisionReference: event.target.value });
            }}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
          <Box>
            <Button type="submit" variant="contained" disabled={createRequest === null}>
              {translate(locale, 'suspension.submit')}
            </Button>
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}
